import React, { useState } from 'react';
import { X, Briefcase, Upload, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientIntakeModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    full_name: '', company_name: '', job_title: '', email: '', phone: '', company_website: '',
    role_title: '', industry: '', employment_type: '', location: '', work_setup: '', timeline: '',
    hiring_reason: '', biggest_challenge: '', worked_with_recruiters: '', salary_range: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const resetForm = () => {
    setForm({
      full_name: '', company_name: '', job_title: '', email: '', phone: '', company_website: '',
      role_title: '', industry: '', employment_type: '', location: '', work_setup: '', timeline: '',
      hiring_reason: '', biggest_challenge: '', worked_with_recruiters: '', salary_range: '',
    });
    setFile(null);
    setSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.company_name || !form.role_title || !form.industry) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('job_description', file);
      const res = await fetch(`${API_URL}/api/contact/employer`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast.success('Your inquiry has been submitted!', { description: "We'll be in touch within 24 hours." });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        data-testid="modal-backdrop"
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        data-testid="client-intake-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-gradient-to-r from-[#0A1F44] to-[#0d2b5c] rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center">
              <Briefcase className="h-5 w-5 text-[#0A1F44]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Request Talent</h2>
              <p className="text-sm text-gray-300">Tell us about your hiring needs</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="modal-close-btn"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <SuccessMessage onClose={handleClose} />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Section 1 */}
              <SectionLabel num="1" title="Contact Information" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Full Name" required>
                  <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Smith" required />
                </FormField>
                <FormField label="Company Name" required>
                  <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Acme Corp" required />
                </FormField>
                <FormField label="Job Title" required>
                  <Input value={form.job_title} onChange={e => set('job_title', e.target.value)} placeholder="VP of Operations" required />
                </FormField>
                <FormField label="Email" required>
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@acme.com" required />
                </FormField>
                <FormField label="Phone Number" required>
                  <Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 123-4567" required />
                </FormField>
                <FormField label="Company Website" optional>
                  <Input value={form.company_website} onChange={e => set('company_website', e.target.value)} placeholder="https://acme.com" />
                </FormField>
              </div>

              {/* Section 2 */}
              <SectionLabel num="2" title="Hiring Needs" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Role Title / Position Needed" required>
                  <Input value={form.role_title} onChange={e => set('role_title', e.target.value)} placeholder="Senior Accountant" required />
                </FormField>
                <FormField label="Industry" required>
                  <Select value={form.industry} onValueChange={v => set('industry', v)}>
                    <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accounting">Accounting</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="logistics">Logistics</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Employment Type" required>
                  <Select value={form.employment_type} onValueChange={v => set('employment_type', v)}>
                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct_hire">Direct Hire</SelectItem>
                      <SelectItem value="talent_pipeline">Exploring Talent Pipeline</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Location of Role" required>
                  <Input value={form.location} onChange={e => set('location', e.target.value)} placeholder="Dallas, TX" required />
                </FormField>
                <FormField label="Work Setup" required>
                  <Select value={form.work_setup} onValueChange={v => set('work_setup', v)}>
                    <SelectTrigger><SelectValue placeholder="Select setup" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="onsite">Onsite</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="remote">Remote</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Target Timeline" required>
                  <Select value={form.timeline} onValueChange={v => set('timeline', v)}>
                    <SelectTrigger><SelectValue placeholder="Select timeline" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent (0–30 days)</SelectItem>
                      <SelectItem value="30-60">30–60 days</SelectItem>
                      <SelectItem value="future">Future planning</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              {/* Section 3 */}
              <SectionLabel num="3" title="Strategic Insight" />
              <div className="space-y-4">
                <FormField label="What is prompting this hire?">
                  <Textarea value={form.hiring_reason} onChange={e => set('hiring_reason', e.target.value)} placeholder="Growth, backfill, new role..." rows={2} />
                </FormField>
                <FormField label="Biggest hiring challenge right now?">
                  <Textarea value={form.biggest_challenge} onChange={e => set('biggest_challenge', e.target.value)} placeholder="Finding qualified candidates, retention..." rows={2} />
                </FormField>
                <FormField label="Have you worked with recruiters before?">
                  <Select value={form.worked_with_recruiters} onValueChange={v => set('worked_with_recruiters', v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              {/* Section 4 */}
              <SectionLabel num="4" title="Optional Details" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Salary Range" optional>
                  <Input value={form.salary_range} onChange={e => set('salary_range', e.target.value)} placeholder="$80,000 - $100,000" />
                </FormField>
                <FormField label="Upload Job Description" optional>
                  <FileInput file={file} setFile={setFile} accept=".pdf,.doc,.docx,.txt" />
                </FormField>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <p className="text-xs text-gray-500 mb-4">
                  By submitting, you agree to be contacted by Omnia Paratus Talent Solutions. We respect your privacy.
                </p>
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold disabled:opacity-50"
                  data-testid="modal-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ num, title }) => (
  <div className="flex items-center gap-3 border-b border-gray-200 pb-2">
    <span className="w-7 h-7 rounded-lg bg-[#0A1F44] text-white text-xs font-bold flex items-center justify-center">{num}</span>
    <h3 className="text-base font-semibold text-[#0A1F44]">{title}</h3>
  </div>
);

const FormField = ({ label, required, optional, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
      {optional && <span className="text-gray-400 ml-1 text-xs">(optional)</span>}
    </label>
    {children}
  </div>
);

const FileInput = ({ file, setFile, accept }) => (
  <div className="relative">
    <label className="flex items-center gap-3 px-4 py-2 border border-gray-200 rounded-md cursor-pointer hover:border-[#D4AF37] transition-colors bg-white">
      <Upload className="h-4 w-4 text-gray-400" />
      <span className="text-sm text-gray-500 truncate">{file ? file.name : 'Choose file...'}</span>
      <input
        type="file"
        accept={accept}
        className="sr-only"
        onChange={e => setFile(e.target.files[0] || null)}
      />
    </label>
  </div>
);

const SuccessMessage = ({ onClose }) => (
  <div className="text-center py-8">
    <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
      <CheckCircle className="h-8 w-8 text-[#D4AF37]" />
    </div>
    <h2 className="text-2xl font-bold text-[#0A1F44] mb-3">Inquiry Received!</h2>
    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
      Thank you for reaching out. We'll review your hiring needs and be in touch within 24 hours.
    </p>
    <Button
      onClick={onClose}
      className="bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"
      data-testid="modal-done-btn"
    >
      Done
    </Button>
  </div>
);

export default ClientIntakeModal;
